import ReactGA from "react-ga4";
import { AnalyticsLogger } from "../logger";
import {
  AnalyticsEventKeys,
  AnalyticsParamKeys,
  AnalyticsParamValues,
  BAM_ONLY_KEYS,
} from "../../constants/analytics";
import { EmarsysManager } from "../emarsys/index";

const { ILS_CURRENCY, UNKNOWN_SOURCE } = AnalyticsParamValues;

const removeUndefined = (obj) => {
  return JSON.parse(
    JSON.stringify(obj, (k, v) => {
      if (v == null || v === "undefined") return undefined;
      return v;
    })
  );
};

const appendQueryParams = (params) => {
  try {
    const search = new URLSearchParams(window.location.search || "");
    return { ...Object.fromEntries(search), ...params };
  } catch (e) {
    return params || {};
  }
};

const toNumber = (value) => {
  return Number(value ?? 0);
};

export const trackFbq = (eventName) => {
  if (window.fbq) {
    window.fbq('track', eventName);
  }
};

class AnalyticsManager {
  analyticsClient = null;
  userProperties = {};
  // Stores promotion data per item_id from SELECT_PROMOTION events
  // Shape: { [item_id]: { promotion_name, promotion_id } }
  promotionStore = {};

  logIgnoredEventsList = [
    AnalyticsEventKeys.VIEW_PROMOTION,
    AnalyticsEventKeys.SELECT_PROMOTION,
  ];

  constructor(analyticsClient) {
    this.analyticsClient = analyticsClient;
  }

  storePromotionData(items, promotionName, promotionId) {
    (items || []).forEach((item) => {
      const id = item?.item_id;
      if (id) {
        this.promotionStore[id] = { promotion_name: promotionName, promotion_id: promotionId };
      }
    });
  }

  applyPromotionData(items) {
    return (items || []).map((item) => {
      const stored = this.promotionStore[item?.item_id];
      if (stored) {
        return { ...item, ...stored };
      }
      return item;
    });
  }

  clearPromotionData(items) {
    (items || []).forEach((item) => {
      delete this.promotionStore[item?.item_id];
    });
  }

  isIgnoredEvent = (eventName) => {
    return this.logIgnoredEventsList.includes(eventName.toLowerCase());
  }

  addIgnoredEvent = (eventName) => {
   eventName.split(',').forEach(event => {
    this.logIgnoredEventsList.push(event);
   });
  }

  event(eventName, payload) {
    payload = payload ? appendQueryParams(payload) : {};
    payload = removeUndefined(payload);

    if (!this.isIgnoredEvent(eventName)) {
      AnalyticsLogger.push(eventName ,{
        event_name: eventName,
        ...payload,
      });  
    }

    try {
      return this.analyticsClient.event(eventName, payload);
    } catch (e) {
      console.error(e);
    }
  }

  gEvent(eventName, payload) {
    try {
      payload = payload ? appendQueryParams(payload) : {};
      payload = removeUndefined(payload);
      return this.analyticsClient.event(eventName, payload);
    } catch (e) {
      console.error(e);
    }
  }

  bam(eventName, payload) {
    AnalyticsLogger.push({
      event_name: eventName,
      ...appendQueryParams(payload || {}),
    });
  }

  gtag(...params) {
    try {
      return this.analyticsClient.gtag(...params);
    } catch (e) {
      console.error(e);
    }
  }

  updateUserProperties(userData) {

    const uid = (userData?.uuid || userData?.id)  ;
    const gender = userData?.gender;
    const cityId = userData?.cityId ;
    const workPlaceId = userData?.workPlaceId ;
    this.userProperties = {
      [AnalyticsParamKeys.USER_ID]: uid ? String(uid) : undefined,
      [AnalyticsParamKeys.USER_GENDER]: gender ? String(gender) : undefined,
      [AnalyticsParamKeys.USER_CITY_ID]: cityId ? String(cityId) : undefined,
      [AnalyticsParamKeys.USER_WORKPLACE_ID]: workPlaceId ? String(workPlaceId) : undefined,
    };
    EmarsysManager.setCustomerId(userData?.uuid);

    return this.gtag("set", "user_properties", this.userProperties);
  }

  enterPage(pageName) {
    return this.event(AnalyticsEventKeys.ENTER_PAGE, {
      pageName: String(pageName),
    });
  }

  logMenuItemPress(itemName) {
    return this.event(AnalyticsEventKeys.MENU_ITEM_PRESS, {
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logMenuCategoryPress(categoryId, categoryTitle) {
    EmarsysManager.emarsysCatergorySelected(categoryTitle);
    return this.event(AnalyticsEventKeys.MENU_CATEGORY_PRESS, {
      [AnalyticsParamKeys.CATEGORY_ID]: categoryId,
      [AnalyticsParamKeys.CATEGORY_TITLE]: categoryTitle,
    });
  }

  logMenuSubCategoryPress(categoryId, categoryTitle) {
    EmarsysManager.emarsysCatergorySelected(categoryTitle);
    return this.event(AnalyticsEventKeys.MENU_SUB_CATEGORY_PRESS, {
      [AnalyticsParamKeys.CATEGORY_ID]: categoryId,
      [AnalyticsParamKeys.CATEGORY_TITLE]: categoryTitle,
    });
  }

  logMenuPersonalZonePress() {
    return this.event(AnalyticsEventKeys.MENU_PERSONAL_ZONE_PRESS);
  }

  logMenuFavoritesPress() {
    return this.event(AnalyticsEventKeys.MENU_FAVORITES_PRESS);
  }

  logPersonalZoneMenuItemPress(itemName) {
    return this.event(AnalyticsEventKeys.PERSONAL_ZONE_MENU_ITEM_PRESS, {
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logEditUserInformation() {
    return this.event(AnalyticsEventKeys.EDIT_USER_INFORMATION);
  }

  logLogin() {
    return this.event(AnalyticsEventKeys.LOGIN, this.userProperties);
  }

  logLoginFailed() {
    return this.event(AnalyticsEventKeys.LOGIN_FAILED);
  }

  logRegister(userData) {
    this.bam(AnalyticsEventKeys.REGISTER, {
      [BAM_ONLY_KEYS.PERSONAL_ID]: userData.user_id,
      [BAM_ONLY_KEYS.FIRST_NAME]: userData.first_name,
      [BAM_ONLY_KEYS.LAST_NAME]: userData.last_name,
      [BAM_ONLY_KEYS.EMAIL]: userData.email,
      [BAM_ONLY_KEYS.CELLPHONE]: userData.phone,
    });

    return this.gEvent(AnalyticsEventKeys.REGISTER);
  }

  logRegisterFailed() {
    return this.event(AnalyticsEventKeys.REGISTER_FAILED);
  }

  logLogout() {
    return this.event(AnalyticsEventKeys.LOGOUT);
  }



  logQuickSearch(searchTerm) {
    EmarsysManager.emarsysSearchTerm(searchTerm);
    return this.event(AnalyticsEventKeys.QUICK_SEARCH, {
      [AnalyticsParamKeys.SEARCH_TERM]: searchTerm,
    });
  }

  logHotSearch(searchTerm) {
    EmarsysManager.emarsysSearchTerm(searchTerm);
    return this.event(AnalyticsEventKeys.HOT_SEARCH, {
      [AnalyticsParamKeys.SEARCH_TERM]: searchTerm,
    });
  }

  logEmptySearch(searchTerm) {
    EmarsysManager.emarsysSearchTerm(searchTerm);
    return this.event(AnalyticsEventKeys.EMPTY_SEARCH, {
      [AnalyticsParamKeys.SEARCH_TERM]: searchTerm,
    });
  }

  logSearchPress(searchTerm) {
    EmarsysManager.emarsysSearchTerm(searchTerm);
    return this.event(AnalyticsEventKeys.SEARCH_PRESS, {
      [AnalyticsParamKeys.SEARCH_TERM]: searchTerm,
    });
  }

  logHomepageTopSliderPress(sliderId, sliderTitle) {
    return this.event(AnalyticsEventKeys.HOMEPAGE_TOP_SLIDER_PRESS, {
      [AnalyticsParamKeys.SLIDER_ID]: sliderId,
      [AnalyticsParamKeys.SLIDER_TITLE]: sliderTitle,
    });
  }

  logHomepageSliderTitlePress(sliderId, sliderTitle, sliderType) {
    return this.event(AnalyticsEventKeys.HOMEPAGE_SLIDER_TITLE_PRESS, {
      [AnalyticsParamKeys.SLIDER_ID]: sliderId,
      [AnalyticsParamKeys.SLIDER_TITLE]: sliderTitle,
      [AnalyticsParamKeys.SLIDER_TYPE]: sliderType,
    });
  }

  logHomepageSliderTabPress(sliderId, sliderTitle, sliderType, itemName) {
    return this.event(AnalyticsEventKeys.HOMEPAGE_SLIDER_TAB_PRESS, {
      [AnalyticsParamKeys.SLIDER_ID]: sliderId,
      [AnalyticsParamKeys.SLIDER_TITLE]: sliderTitle,
      [AnalyticsParamKeys.SLIDER_TYPE]: sliderType,
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logHomepageSliderButtonPress(sliderId, sliderTitle, sliderType, itemName) {
    return this.event(AnalyticsEventKeys.HOMEPAGE_SLIDER_BUTTON_PRESS, {
      [AnalyticsParamKeys.SLIDER_ID]: sliderId,
      [AnalyticsParamKeys.SLIDER_TITLE]: sliderTitle,
      [AnalyticsParamKeys.SLIDER_TYPE]: sliderType,
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logShareUrl(contentType, method, itemId) {
    return this.event(AnalyticsParamValues.SHARE, {
      [AnalyticsParamKeys.METHOD]: method,
      [AnalyticsParamKeys.CONTENT_TYPE]: contentType,
      [AnalyticsParamKeys.ITEM_ID]: itemId,
    });
  }

  logHomepageSliderPromotionSlidePress(
    sliderId,
    sliderTitle,
    sliderType,
    tabId,
    tabTitle
  ) {
    return this.gEvent(
      AnalyticsEventKeys.HOMEPAGE_SLIDER_PROMOTION_SLIDE_PRESS,
      {
        [AnalyticsParamKeys.SLIDER_ID]: sliderId,
        [AnalyticsParamKeys.SLIDER_TITLE]: sliderTitle,
        [AnalyticsParamKeys.SLIDER_TYPE]: sliderType,
        [AnalyticsParamKeys.TAB_ID]: tabId,
        [AnalyticsParamKeys.TAB_TITLE]: tabTitle,
      }
    );
  }

  logBannerPress(bannerId) {
    return this.event(AnalyticsEventKeys.BANNER_PRESS, {
      [AnalyticsParamKeys.BANNER_ID]: bannerId,
    });
  }

  logViewItem(itemsList) {
    EmarsysManager.trackItemView(itemsList[0]);
    const enrichedItems = this.applyPromotionData(itemsList);
    return this.event(AnalyticsEventKeys.VIEW_ITEM, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: enrichedItems,
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
      [AnalyticsParamKeys.VALUE]: toNumber(itemsList[0].price),
    });
  }

  //click on top banner gallery - 12.1.2
  logSelectPromotionTopGallery(
    itemsList,
    createSlot = "gallery_1",
    promotionName = "גלריית דף הבית"
  ) {
    return this.logSelectPromotion(itemsList, createSlot, promotionName);
  }

  logSelectPromotionBannerSliderHomepage(itemsList, createSlot, promotionName) {
    return this.logSelectPromotion(itemsList, createSlot, promotionName);
  }

  logClickOnBenefitHomepageSlider(itemsList, creativeSlot, promotionName) {
    return this.logSelectPromotion(itemsList, creativeSlot, promotionName);
  }

  logGalleryCarouselScrollForward(creativeSlot = "gallery_1") {
    return this.gEvent(AnalyticsEventKeys.GALLERY_CAROUSEL_SCROLL_FORWARD, {
      [AnalyticsParamKeys.LOCATION]: creativeSlot,
    });
  }

  logGalleryCarouselScrollBack(creativeSlot = "gallery_1") {
    return this.gEvent(AnalyticsEventKeys.GALLERY_CAROUSEL_SCROLL_BACK, {
      [AnalyticsParamKeys.LOCATION]: creativeSlot,
    });
  }

  logPromotionPress(eventKey, itemsList, creativeSlot, promotionName, recommendationsPromotionsType) {
    try {
      if (!itemsList || !creativeSlot || !promotionName) return;
      itemsList = Array.isArray(itemsList) ? itemsList : [itemsList];
      const items = (itemsList || [])
        .flat()
        .filter((item) => item?.item_name || item?.item_id)
        .map((item) => {
          Object
          .keys(item)
          .forEach((key) => {
            if (item[key] == undefined) {
              item[key] = '';
            }
          });
          return ({
            ...item,
            item_category: item.item_category || '',
            item_name: item.item_name || '',
            item_id: item.item_id || '',
            item_variant: item.item_variant || '',
            item_brand: item.item_brand || '',
            item_list_name: item.item_list_name || promotionName,
          })
        });

      if (items.length === 0) return;
      const payload = {
        [AnalyticsParamKeys.CREATIVE_SLOT]: creativeSlot,
        [AnalyticsParamKeys.PROMOTION_NAME]: promotionName,
        [AnalyticsParamKeys.ITEMS]: items,
      };

      if (recommendationsPromotionsType != null) {
        payload[AnalyticsParamKeys.PROMOTION_ID] = recommendationsPromotionsType;
      }

      return this.gEvent(eventKey, payload);
    } catch (error) {
      console.error(error);
    }
  }

  logViewPromotion(itemsList, creativeSlot, promotionName, recommendationsPromotionsType) {
    return this.logPromotionPress(
      AnalyticsEventKeys.VIEW_PROMOTION,
      itemsList,
      creativeSlot,
      promotionName,
      recommendationsPromotionsType
    );
  }

  logSelectPromotion(itemsList, creativeSlot, promotionName, recommendationsPromotionsType) {
    const normalizedItems = Array.isArray(itemsList) ? itemsList.flat() : [itemsList];
    this.storePromotionData(normalizedItems, promotionName, recommendationsPromotionsType);
    return this.logPromotionPress(
      AnalyticsEventKeys.SELECT_PROMOTION,
      itemsList,
      creativeSlot,
      promotionName,
      recommendationsPromotionsType
    );
  }

  //12.1.1 - view top gallery
  logBannersSlidersHomePage(
    itemsList,
    creativeSlot = "gallery_1",
    promotionName = "גלריית דף הבית"
  ) {
    return this.logViewPromotion(itemsList, creativeSlot, promotionName);
  }

  logViewMainSliderHomePage(itemsList, creativeSlot, promotionName, recommendationsPromotionsType) {
    //itemsList - benefits list in main tab slider
    //creative slot - slider_x (slider_1, slider_2...)
    //promotion_name = slider title
    return this.logViewPromotion(itemsList, creativeSlot, promotionName, recommendationsPromotionsType);
  }

  logViewMainSliderTabHomePage(itemsList, creativeSlot, promotionName) {
    //itemsList - benefits list in main tab slider
    //creative slot - slider_x (slider_1, slider_2...)
    //promotion_name = slider title_tab_title
    return this.logViewPromotion(itemsList, creativeSlot, promotionName);
  }

  logViewBannerSliderHomePage(itemsList, creativeSlot, promotionName) {
    //itemsList - benefit/category/external like banners in the gallery
    //creative slot - slider_between_x (slider_between_1, slider_between_2...)
    //promotion_name = slider title
    return this.logViewPromotion(itemsList, creativeSlot, promotionName);
  }

  logSliderCarouselScrollForward(creativeSlot) {
    return this.gEvent(AnalyticsEventKeys.SLIDER_CAROUSEL_SCROLL_FORWARD, {
      [AnalyticsParamKeys.LOCATION]: creativeSlot,
    });
  }

  logSliderCarouselScrollBack(creativeSlot) {
    return this.gEvent(AnalyticsEventKeys.SLIDER_CAROUSEL_SCROLL_BACK, {
      [AnalyticsParamKeys.LOCATION]: creativeSlot,
    });
  }

  logAddToCart(itemsList, totalPrice = 1) {
    EmarsysManager.emarsysAddToCart(itemsList);
    const enrichedItems = this.applyPromotionData(itemsList);
    return this.event(AnalyticsEventKeys.ADD_TO_CART, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: enrichedItems,
      [AnalyticsParamKeys.VALUE]: toNumber(totalPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logUpsalePopupPresented(status) {
    return this.event(AnalyticsEventKeys.UPSALE_POPUP_PRESENTED, {
      [AnalyticsParamKeys.STATUS]: status,
    });
  }
  logUpsalePopupViewedItemList(itemsList) {
    return this.event(AnalyticsEventKeys.VIEW_ITEM_LIST, {
      [AnalyticsParamKeys.ITEM_LIST_NAME]: "upsale_popup",
      [AnalyticsParamKeys.ITEMS]: itemsList,
    });
  }
  logUpsalePopupProductClick(status) {
    return this.event(AnalyticsEventKeys.UPSALE_POPUP_PRODUCT_CLICK, {
      [AnalyticsParamKeys.STATUS]: status,
    });
  }

  logSelectItem(itemsList) {
    return this.event(AnalyticsEventKeys.SELECT_ITEM, {
      [AnalyticsParamKeys.ITEMS]: itemsList,
    });
  }

  logClickOnBranchPhoneNumber(itemsList, totalPrice = 0) {
    return this.event(AnalyticsEventKeys.ADD_TO_CART, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.VALUE]: toNumber(totalPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logClickOnBranchAddress(itemsList, totalPrice = 0) {
    return this.event(AnalyticsEventKeys.ADD_TO_CART, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.VALUE]: toNumber(totalPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logClickOLeaveDetails(itemsList, totalPrice = 0) {
    return this.event(AnalyticsEventKeys.ADD_TO_CART, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.VALUE]: toNumber(totalPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logLeadSubmitSuccess(itemsList, totalPrice = 0) {
    return this.event(AnalyticsEventKeys.BEGIN_CHECKOUT, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.VALUE]: toNumber(totalPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logPurchase(itemsList, totalPrice = 1, transactionId = null) {
    EmarsysManager.trackPurchase(itemsList, transactionId);
    const enrichedItems = this.applyPromotionData(itemsList);
    this.clearPromotionData(itemsList);
    return this.event(AnalyticsEventKeys.PURCHASE, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: enrichedItems,
      [AnalyticsParamKeys.VALUE]: toNumber(totalPrice),
      [AnalyticsParamKeys.TRANSACTION_ID]: transactionId,
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logBenefitPress(
    benefitId,
    benefitTitle,
    maRecommended,
    pressSource = UNKNOWN_SOURCE
  ) {
    return true;
  }

  logProductPress(productId, productTitle, pressSource = UNKNOWN_SOURCE) {
    return true;
    return this.event(AnalyticsEventKeys.PRODUCT_PRESS, {
      [AnalyticsParamKeys.PRODUCT_ID]: productId,
      [AnalyticsParamKeys.PRODUCT_TITLE]: productTitle,
      [AnalyticsParamKeys.PRESS_SOURCE]: pressSource,
    });
  }

  logAddToFavorites(itemsList) {
    return this.event(AnalyticsEventKeys.ADD_TO_FAVORITES, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
      [AnalyticsParamKeys.VALUE]: toNumber(itemsList[0].price),
    });
  }

  logAddToWishlist(itemsList) {
    return this.event(AnalyticsEventKeys.ADD_TO_WISHLIST, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
      [AnalyticsParamKeys.VALUE]: 0,
    });
  }

  logRemoveFromFavorites(itemsList) {
    return this.event(AnalyticsEventKeys.REMOVE_FROM_FAVORITES, {
      ...this.userProperties,
      [AnalyticsParamKeys.ITEMS]: itemsList,
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
      [AnalyticsParamKeys.VALUE]: toNumber(itemsList[0].price),
    });
  }

  logSubscribeToNewsletter(params) {
    return this.bam(AnalyticsEventKeys.SUBSCRIBE_NEWSLETTER, params);
  }

  logSubscribeToNewsletterEvents() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_SUBMITTED, {
      [AnalyticsParamKeys.LOCATION]: "footer",
    });
  }

  logSubscribeToNewsletterSuccess() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_SUCCESS, {
      [AnalyticsParamKeys.LOCATION]: "footer",
    });
  }

  logSubscribeToNewsletterFailed() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_FAILED, {
      [AnalyticsParamKeys.LOCATION]: "footer",
    });
  }

  logSubscribeToNewsletterPopupSubmitted() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_SUBMITTED, {
      [AnalyticsParamKeys.LOCATION]: "popup",
    });
  }
  logSubscribeToNewsletterPresented() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_PRESENTED, {
      [AnalyticsParamKeys.LOCATION]: "popup",
    });
  }

  logSubscribeToNewsletterPopupSuccess() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_SUCCESS, {
      [AnalyticsParamKeys.LOCATION]: "popup",
    });
  }

  logSubscribeToNewsletterPopupFailed() {
    return this.event(AnalyticsEventKeys.NEWSLETTER_SIGNUP_FAILED, {
      [AnalyticsParamKeys.LOCATION]: "popup",
    });
  }
  logBenefitWebsiteOpen(benefitId, benefitTitle) {
    return this.event(AnalyticsEventKeys.BENEFIT_WEBSITE_OPEN, {
      [AnalyticsParamKeys.BENEFIT_ID]: benefitId,
      [AnalyticsParamKeys.BENEFIT_TITLE]: benefitTitle,
    });
  }

  logBenefitGetCouponCodePress(benefitId, benefitTitle) {
    return this.event(AnalyticsEventKeys.BENEFIT_GET_COUPON_CODE_PRESS, {
      [AnalyticsParamKeys.BENEFIT_ID]: benefitId,
      [AnalyticsParamKeys.BENEFIT_TITLE]: benefitTitle,
    });
  }

  logBenefitGetPaidCouponPress(benefitId, benefitTitle) {
    return this.event(AnalyticsEventKeys.BENEFIT_GET_PAID_COUPON_PRESS, {
      [AnalyticsParamKeys.BENEFIT_ID]: benefitId,
      [AnalyticsParamKeys.BENEFIT_TITLE]: benefitTitle,
    });
  }

  logBenefitGetPaidCouponPressWithoutCard(benefitId, benefitTitle) {
    return this.event(AnalyticsEventKeys.CREDIT_CARD_POPUP_PRESENTED);
  }

  logBenefitPaidCouponEditUserCard(benefitId, benefitTitle) {
    return this.event(AnalyticsEventKeys.BENEFIT_PAID_COUPON_EDIT_USER_CARD, {
      [AnalyticsParamKeys.BENEFIT_ID]: benefitId,
      [AnalyticsParamKeys.BENEFIT_TITLE]: benefitTitle,
    });
  }

  logBenefitPaidCouponSuccess(benefitId, benefitPrice) {
    return this.event(AnalyticsEventKeys.BENEFIT_PAID_COUPON_SUCCESS, {
      [AnalyticsParamKeys.BENEFIT_ID]: benefitId,
      [AnalyticsParamKeys.VALUE]: toNumber(benefitPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logBenefitPaidCouponFailed(benefitId) {
    return this.event(AnalyticsEventKeys.BENEFIT_PAID_COUPON_FAILED, {
      [AnalyticsParamKeys.BENEFIT_ID]: benefitId,
    });
  }

  logProductBuyWithClickPress(productId, productTitle) {
    return this.event(AnalyticsEventKeys.PRODUCT_BUY_WITH_CLICK_PRESS, {
      [AnalyticsParamKeys.PRODUCT_ID]: productId,
      [AnalyticsParamKeys.PRODUCT_TITLE]: productTitle,
    });
  }

  logProductBuyWithClickSuccess(productId, productPrice) {
    return this.event(AnalyticsEventKeys.PRODUCT_BUY_WITH_CLICK_SUCCESS, {
      [AnalyticsParamKeys.PRODUCT_ID]: productId,
      [AnalyticsParamKeys.VALUE]: toNumber(productPrice),
      [AnalyticsParamKeys.CURRENCY]: ILS_CURRENCY,
    });
  }

  logProductBuyWithClickFailed(productId) {
    return this.event(AnalyticsEventKeys.PRODUCT_BUY_WITH_CLICK_FAILED, {
      [AnalyticsParamKeys.PRODUCT_ID]: productId,
    });
  }

  logMagazineSliderPress() {
    return this.event(AnalyticsEventKeys.MAGAZINE_SLIDER_PRESS);
  }

  logArticlePress(articleId, articleTitle, pressSource = UNKNOWN_SOURCE) {
    return this.event(AnalyticsEventKeys.ARTICLE_PRESS, {
      [AnalyticsParamKeys.ARTICLE_ID]: articleId,
      [AnalyticsParamKeys.ARTICLE_TITLE]: articleTitle,
      [AnalyticsParamKeys.PRESS_SOURCE]: pressSource,
    });
  }

  logVideoArticlePress(
    videoArticleId,
    videoArticleTitle,
    pressSource = UNKNOWN_SOURCE
  ) {
    return this.event(AnalyticsEventKeys.VIDEO_ARTICLE_PRESS, {
      [AnalyticsParamKeys.VIDEO_ARTICLE_ID]: videoArticleId,
      [AnalyticsParamKeys.VIDEO_ARTICLE_TITLE]: videoArticleTitle,
      [AnalyticsParamKeys.PRESS_SOURCE]: pressSource,
    });
  }

  logInsuranceSliderPress() {
    return this.event(AnalyticsEventKeys.INSURANCE_SLIDER_PRESS);
  }

  logInsuranceSliderItemPress(itemName) {
    return this.event(AnalyticsEventKeys.INSURANCE_SLIDER_ITEM_PRESS, {
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logLeadVendor() {
    return this.event(AnalyticsEventKeys.LEAD_VENDOR);
  }

  logLeadUser(leadData) {
    this.bam(AnalyticsEventKeys.LEAD_USER, {
      [BAM_ONLY_KEYS.PERSONAL_ID]: leadData.personal_id,
      [BAM_ONLY_KEYS.FIRST_NAME]: leadData.first_name,
      [BAM_ONLY_KEYS.LAST_NAME]: leadData.last_name,
      [BAM_ONLY_KEYS.CELLPHONE]: leadData.cellphone,
      [BAM_ONLY_KEYS.EMAIL]: leadData.email,
    });
    return this.gEvent(AnalyticsEventKeys.LEAD_USER, {
      [AnalyticsParamKeys.REQUEST_REASON]: leadData.reason,
    });
  }

  logFooterItemPress(itemName) {
    return this.event(AnalyticsEventKeys.FOOTER_ITEM_PRESS, {
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logAppStoreLinkPress(itemName) {
    return this.event(AnalyticsEventKeys.APP_STORE_LINK_PRESS, {
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logSocialMediaLinkPress(itemName) {
    return this.event(AnalyticsEventKeys.SOCIAL_MEDIA_LINK_PRESS, {
      [AnalyticsParamKeys.ITEM_NAME]: itemName,
    });
  }

  logContactUsMessageSent() {
    return this.event(AnalyticsEventKeys.CONTACT_US_MESSAGE_SENT);
  }

  logRequestMembershipCard() {
    return this.event(AnalyticsEventKeys.REQUEST_MEMBERSHIP_CARD);
  }

  logRequestAppDownload() {
    return this.event(AnalyticsEventKeys.REQUEST_APP_DOWNLOAD);
  }

  logException(error) {
    return this.event(AnalyticsEventKeys.EXCEPTION, {
      description: error,
      fatal: false,
      message: error.message,
    });
  }
}

export default new AnalyticsManager(ReactGA);
